import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Col, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { H1, Text } from '@amzn/stencil-react-components/text'

import {
    BucketsAndCupsGroupDTO,
    isBucketsAndCupsGroupEntity,
} from 'src/models/dto/activities/BucketsAndCupsGroupDTO'
import { ActivityType } from 'src/models/dto/ActivityDTO'
import {
    BucketsAndCupsVisualizer,
    PageItemSummary,
} from 'src/pages/module-builder/buckets-and-cups-editor/BucketsAndCupsVisualizer'
import { loadPoolPageResolver } from 'src/pages/module-builder/buckets-and-cups-editor/ItemContainerPageResolver'
import { ModuleService } from 'src/services/backend/ModuleService'
import { BucketsAndCupsSelectionPlanService } from 'src/services/bucketsAndCups/BucketsAndCupsSelectionPlanService'

interface VisualizerData {
    moduleName: string
    group: BucketsAndCupsGroupDTO
    resolvePageItems: (pageLabel: string) => PageItemSummary[]
}

/**
 * Standalone Buckets & Cups visualizer page.
 *
 * Route: /buckets-and-cups-visualizer/:moduleVersionId
 *
 * Reads the module via the GetModuleVersion API, finds its Buckets & Cups group
 * (and its ItemPool version id), pulls the pool's item container modules, and
 * resolves every cup's page references to the items on those pages.
 */
export const BucketsAndCupsVisualizerPage = () => {
    const { moduleVersionId } = useParams<{ moduleVersionId: string }>()
    const [data, setData] = useState<VisualizerData | undefined>(undefined)
    const [error, setError] = useState<string | undefined>(undefined)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let active = true
        const load = async () => {
            if (!moduleVersionId) {
                setError('No module version id in the URL.')
                setLoading(false)
                return
            }
            setLoading(true)
            setError(undefined)
            try {
                // 1. Fetch the module (GetModuleVersion API).
                const moduleVersion = await ModuleService.loadModuleVersionDTO(moduleVersionId)
                const content = moduleVersion.content

                // 2. Find the Buckets & Cups group in the module's workflow.
                const bcActivity = content.workflow.find(
                    (activity) => activity.moduleChild.type === ActivityType.LaunchBucketsAndCups
                )
                if (!bcActivity || !isBucketsAndCupsGroupEntity(bcActivity.moduleChild)) {
                    throw new Error('This module has no Buckets & Cups selection group.')
                }
                const group = bcActivity.moduleChild

                // 3. Resolve the ItemPool's pages to the items delivered on each page.
                const resolvePageItems = await loadPoolPageResolver(group.poolVersionId)

                if (active) {
                    setData({
                        moduleName: content.name,
                        group,
                        resolvePageItems,
                    })
                }
            } catch (e) {
                if (active) {
                    setError(e instanceof Error ? e.message : 'Failed to load the module.')
                }
            } finally {
                if (active) {
                    setLoading(false)
                }
            }
        }
        void load()
        return () => {
            active = false
        }
    }, [moduleVersionId])

    return (
        <View padding='S400'>
            <Col gridGap='S300'>
                <Col gridGap='S100'>
                    <H1 fontSize='T500'>Buckets &amp; Cups{data ? ` — ${data.moduleName}` : ''}</H1>
                    <Text fontSize='T200' color='neutral70'>
                        Interactive view of the module&apos;s selection structure and scoring,
                        resolved against the ItemPool. Use “Simulate another draw” to run the
                        backend selection and highlight the drawn buckets, cups, and pages.
                    </Text>
                </Col>

                {loading && (
                    <Col gridGap='S200' alignItems='center' padding='S400'>
                        <Spinner />
                        <Text fontSize='T100' color='neutral70'>
                            Loading module and item containers…
                        </Text>
                    </Col>
                )}

                {!loading && error && (
                    <MessageBanner type={MessageBannerType.Error}>{error}</MessageBanner>
                )}

                {!loading && !error && data && (
                    <BucketsAndCupsVisualizer
                        group={data.group}
                        resolvePageItems={data.resolvePageItems}
                        generateSelectionPlan={() =>
                            BucketsAndCupsSelectionPlanService.generateSelectionPlan(data.group)
                        }
                    />
                )}
            </Col>
        </View>
    )
}

BucketsAndCupsVisualizerPage.displayName = 'BucketsAndCupsVisualizerPage'
